import React from 'react';
import useGreetingDismiss from 'hooks/useGreetingDismiss';
import useLocalGreeting from 'hooks/useLocalGreeting';
import { Line, Name, Phrase, Roman, Wrapper } from './styles';

/**
 * "Guten Morgen, Jenny" at the top of the dashboard — hello in the language of wherever the
 * person happens to be, and nothing else translated.
 *
 * It fades in, holds for a few seconds and then gives its space back to the programs below.
 * Kept visually quieter than the featured program title on purpose: the greeting is the first
 * thing you read, not the thing you came for.
 *
 * Non-Latin scripts get a small transliteration alongside so the line is never unreadable,
 * and the whole thing carries an English `aria-label` so a screen reader doesn't try to
 * pronounce Japanese in an English voice.
 */
const Greeting: React.FC = () => {
  // Phrase and name arrive together from the hook, so the line is never half-updated.
  const greeting = useLocalGreeting();
  const isVisible = useGreetingDismiss(Boolean(greeting));

  const firstName = greeting?.firstName;
  const label = greeting ? `${greeting.meaning}${firstName ? `, ${firstName}` : ''}` : undefined;

  return (
    <Wrapper isVisible={isVisible} aria-hidden={!isVisible}>
      <Line isReady={Boolean(greeting)} aria-label={label} title={label}>
        {greeting && (
          <>
            {/* The comma sits outside the phrase so an RTL greeting doesn't render it
                on the wrong side of the name. */}
            <Phrase aria-hidden>
              <span lang={greeting.tag} dir={greeting.rtl ? 'rtl' : undefined}>
                {greeting.text}
              </span>
              {firstName ? ',' : ''}
            </Phrase>
            {firstName && <Name aria-hidden>{firstName}</Name>}
            {greeting.roman && <Roman aria-hidden>{greeting.roman}</Roman>}
          </>
        )}
      </Line>
    </Wrapper>
  );
};

export default Greeting;
